import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/base/Modal";
import { money, variantLabel } from "@/lib/format";
import { linkSupplierProduct, unlinkSupplierProduct } from "@/lib/api/supplier-products";
import { inputCls, labelCls, type SupplierLinkRow, type VariantRow } from "../types";

// Self-contained "Suppliers" feature: links a product to suppliers and manages
// those links. Receives the resolved link/supplier/variant data from the page
// and owns its own link mutations + modal.

interface Props {
  productId: string;
  orgId: string;
  variants: VariantRow[];
  links: SupplierLinkRow[];
  suppliers: { id: string; name: string }[];
}

export default function SuppliersTab({ productId, orgId, variants, links, suppliers }: Props) {
  const queryClient = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [linkDeleting, setLinkDeleting] = useState<string | null>(null);
  const [linkForm, setLinkForm] = useState({ supplier_id: "", variant_id: "", supplier_sku: "", supplier_price: "", min_order_qty: "", order_multiple: "", lead_time_days: "", is_preferred: false });

  const addSupplierLink = async (e: FormEvent) => {
    e.preventDefault();
    setLinkError("");
    if (!linkForm.supplier_id) {
      setLinkError("Select a supplier.");
      return;
    }
    setLinkSaving(true);
    try {
      await linkSupplierProduct(orgId, {
        productId,
        supplierId: linkForm.supplier_id,
        variantId: linkForm.variant_id || null,
        supplierSku: linkForm.supplier_sku || null,
        supplierPrice: linkForm.supplier_price ? Number(linkForm.supplier_price) : null,
        minOrderQty: linkForm.min_order_qty ? Number(linkForm.min_order_qty) : 1,
        orderMultiple: linkForm.order_multiple ? Number(linkForm.order_multiple) : null,
        leadTimeDays: linkForm.lead_time_days ? Number(linkForm.lead_time_days) : null,
        isPreferred: linkForm.is_preferred,
      });
    } catch (err) {
      setLinkSaving(false);
      setLinkError((err as Error).message);
      return;
    }
    setLinkSaving(false);
    setLinkOpen(false);
    setLinkForm({ supplier_id: "", variant_id: "", supplier_sku: "", supplier_price: "", min_order_qty: "", order_multiple: "", lead_time_days: "", is_preferred: false });
    queryClient.invalidateQueries({ queryKey: ["product-suppliers", orgId, productId] });
  };

  const removeSupplierLink = async (linkId: string) => {
    setLinkDeleting(linkId);
    try {
      await unlinkSupplierProduct(orgId, linkId);
    } catch {
      // deletion failures are non-fatal to the UI; the list simply stays in sync on next refetch
    }
    setLinkDeleting(null);
    queryClient.invalidateQueries({ queryKey: ["product-suppliers", orgId, productId] });
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Suppliers</h3>
            <p className="text-xs text-slate-400 mt-0.5">Suppliers linked to this product with negotiated price and minimum order.</p>
          </div>
          <button
            onClick={() => { setLinkError(""); setLinkOpen(true); }}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-add-line" /> Link supplier
          </button>
        </div>
        {(links ?? []).length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No suppliers linked yet</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {(links ?? []).map((l) => (
              <div key={l.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/suppliers/${l.supplier_id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700 truncate">
                    {l.suppliers?.name ?? "—"}
                  </Link>
                  {l.is_preferred && <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full ml-2">Preferred</span>}
                  {l.supplier_sku && <span className="text-xs text-slate-400 font-mono ml-2">{l.supplier_sku}</span>}
                  {l.variants?.sku && <span className="text-xs text-slate-400 font-mono ml-2">· {l.variants.sku}</span>}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-800">{l.supplier_price != null ? money(l.supplier_price) : "—"}</p>
                    <p className="text-xs text-slate-400">
                      {l.min_order_qty ? `min ${l.min_order_qty}` : ""}
                      {l.order_multiple && l.order_multiple > 1 ? ` · ×${l.order_multiple}` : ""}
                      {l.lead_time_days ? ` · ${l.lead_time_days}d` : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => removeSupplierLink(l.id)}
                    disabled={linkDeleting === l.id}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        title="Link Supplier"
        footer={
          <>
            <button onClick={() => setLinkOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={addSupplierLink} disabled={linkSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {linkSaving ? "Linking..." : "Link Supplier"}
            </button>
          </>
        }
      >
        <form onSubmit={addSupplierLink} className="space-y-4">
          {linkError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{linkError}</div>}
          <div>
            <label className={labelCls}>Supplier *</label>
            <select required value={linkForm.supplier_id} onChange={(e) => setLinkForm((f) => ({ ...f, supplier_id: e.target.value }))} className={inputCls}>
              <option value="">Select supplier...</option>
              {(suppliers ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Applies to variant (optional)</label>
            <select value={linkForm.variant_id} onChange={(e) => setLinkForm((f) => ({ ...f, variant_id: e.target.value }))} className={inputCls}>
              <option value="">All variants</option>
              {(variants ?? []).map((v) => (
                <option key={v.id} value={v.id}>{v.sku ?? variantLabel(v.option_values) ?? "Variant"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Supplier SKU</label>
            <input value={linkForm.supplier_sku} onChange={(e) => setLinkForm((f) => ({ ...f, supplier_sku: e.target.value }))} className={inputCls} placeholder="SUP-001" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Supplier price</label>
              <input type="number" step="0.01" min="0" value={linkForm.supplier_price} onChange={(e) => setLinkForm((f) => ({ ...f, supplier_price: e.target.value }))} className={inputCls} placeholder="0.00" />
            </div>
            <div>
              <label className={labelCls}>Min order qty</label>
              <input type="number" min="0" value={linkForm.min_order_qty} onChange={(e) => setLinkForm((f) => ({ ...f, min_order_qty: e.target.value }))} className={inputCls} placeholder="1" />
            </div>
            <div>
              <label className={labelCls}>Order multiple</label>
              <input type="number" min="1" value={linkForm.order_multiple} onChange={(e) => setLinkForm((f) => ({ ...f, order_multiple: e.target.value }))} className={inputCls} placeholder="1" />
            </div>
            <div>
              <label className={labelCls}>Lead time (days)</label>
              <input type="number" min="0" value={linkForm.lead_time_days} onChange={(e) => setLinkForm((f) => ({ ...f, lead_time_days: e.target.value }))} className={inputCls} placeholder="14" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={linkForm.is_preferred} onChange={(e) => setLinkForm((f) => ({ ...f, is_preferred: e.target.checked }))} className="w-4 h-4 rounded accent-emerald-600" />
            Preferred supplier
          </label>
        </form>
      </Modal>
    </>
  );
}